import { useContext, useEffect } from "react";

import { MapContext, MapLoadedContext } from "./Map";
export interface TileLayerMetadata {
  tileUrls: string[];
  minzoom?: number;
  maxzoom?: number;
  bounds?: number[];
}

const VectorLayer = (props: {
  namePrefixes: string[];
  chipTile?: TileLayerMetadata;
  annotationTile?: TileLayerMetadata;
  annotationLayerName: string;
  annotationConfidenceStart: number;
  annotationConfidenceEnd: number;
  preFilteringConfidenceStart: number;
  preFilteringConfidenceEnd: number;
  annotationStartDate: string | null;
  annotationEndDate: string | null;
  selectedClassTypes: Set<string>;
}) => {
  const {
    namePrefixes,
    chipTile,
    annotationTile,
    annotationLayerName,
    annotationConfidenceStart,
    annotationConfidenceEnd,
    preFilteringConfidenceStart,
    preFilteringConfidenceEnd,
    annotationStartDate,
    annotationEndDate,
    selectedClassTypes,
  } = props;
  const map = useContext(MapContext);
  const mapLoaded = useContext(MapLoadedContext);
  // Add chip source & layers
  useEffect(() => {
    if (!mapLoaded || !map) {
      return;
    }
    var layers = map.getStyle().layers;
    // Find the index of the first symbol layer in the map style
    // This has the effect of inserting the new layers behind symbols like
    // place names and road names
    let firstSymbolId: string | undefined = undefined;
    if (layers) {
      for (var i = 0; i < layers.length; i++) {
        if (layers[i].type === "symbol") {
          firstSymbolId = layers[i].id;
          break;
        }
      }
    }
    if (chipTile && !map.getSource("chips")) {
      map.addSource("chips", {
        type: "vector",
        tiles: chipTile.tileUrls,
        minzoom: chipTile.minzoom,
        maxzoom: chipTile.maxzoom,
        bounds: chipTile.bounds,
      });
    }

    namePrefixes.forEach((namePrefix) => {
      map.addLayer(
        {
          id: `${namePrefix}-polygon-layer`,
          type: "fill",
          source: "chips",
          "source-layer": `${namePrefix.replace(/[.-]/g, "")}`,
          filter: ["==", "$type", "Polygon"],
          paint: {
            "fill-color": "rgba(86, 126, 196, 0.3)",
            "fill-antialias": true,
            "fill-outline-color": "rgba(86, 126, 196, 0.5)",
          },
        },
        firstSymbolId
      );
    });

    return () => {
      namePrefixes.forEach((namePrefix) => {
        map.setFilter(`${namePrefix}-polygon-layer`, null);
        map.removeLayer(`${namePrefix}-polygon-layer`);
      });

      if (map.getSource("chips")) {
        map.removeSource("chips");
      }
    };
  }, [map, mapLoaded, namePrefixes, chipTile]);

  // Add annotation source & layers
  useEffect(() => {
    if (!mapLoaded || !map) {
      return;
    }
    var layers = map.getStyle().layers;
    let firstSymbolId: string | undefined = undefined;
    if (layers) {
      for (var i = 0; i < layers.length; i++) {
        if (layers[i].type === "symbol") {
          firstSymbolId = layers[i].id;
          break;
        }
      }
    }
    if (
      annotationTile &&
      annotationTile.tileUrls.length > 0 &&
      !map.getSource("annotations")
    ) {
      map.addSource("annotations", {
        type: "vector",
        tiles: annotationTile.tileUrls,
        minzoom: annotationTile.minzoom,
        maxzoom: annotationTile.maxzoom,
      });
    }
    if (map.getSource("annotations")) {
      map.addLayer(
        {
          id: `${annotationLayerName}-layer`,
          type: "fill",
          source: "annotations",
          "source-layer": annotationLayerName,
          filter: ["==", "$type", "Polygon"],
          paint: {
            "fill-color": "rgba(186, 46, 15, 0.3)",
            "fill-antialias": true,
            "fill-outline-color": "rgba(186, 46, 15, 0.3)",
          },
        },
        firstSymbolId
      );
    }

    return () => {
      if (map.getLayer(`${annotationLayerName}-layer`)) {
        map.setFilter(`${annotationLayerName}-layer`, null);
        map.removeLayer(`${annotationLayerName}-layer`);
      }
      if (map.getSource("annotations")) {
        map.removeSource("annotations");
      }
    };
  }, [annotationLayerName, map, mapLoaded, annotationTile]);

  // Filter annotations layer
  useEffect(() => {
    if (!map || !mapLoaded) {
      return;
    }
    const layerName = `${annotationLayerName}-layer`;
    const annotationLayer = map.getLayer(layerName);
    if (!annotationLayer) {
      return;
    }
    const filters = [];
    // Filter by annotation confidence score
    filters.push([
      "all",
      [">=", ["number", ["get", "confidence"], 0], annotationConfidenceStart],
      ["<", ["number", ["get", "confidence"], 0], annotationConfidenceEnd],
    ]);
    // Filter by class name
    filters.push([
      "in",
      ["coalesce", ["get", "class"], ["get", "class_name"]],
      ["literal", Array.from(selectedClassTypes)],
    ]);

    // Filter by annotation date
    if (annotationStartDate && annotationEndDate) {
      filters.push([
        "all",
        [">=", ["get", "creation_date"], annotationStartDate],
        ["<", ["get", "creation_date"], annotationEndDate],
      ]);
    }

    map.setFilter(layerName, ["all", ...filters]);
    return () => {
      map.setFilter(layerName, null);
    };
  }, [
    annotationLayerName,
    map,
    mapLoaded,
    annotationConfidenceStart,
    annotationConfidenceEnd,
    annotationStartDate,
    annotationEndDate,
    selectedClassTypes,
    namePrefixes,
  ]);

  // Filter objects by pre-filtering confidence score
  useEffect(() => {
    if (!map || !mapLoaded) {
      return;
    }
    const layerNames = namePrefixes.map(
      (namePrefix) => `${namePrefix}-polygon-layer`
    );
    layerNames.forEach((layerName) => {
      const chipsSource = map.getSource("chips");
      if (!chipsSource) {
        return;
      }
      // Only show points if that are within the pre_filter_range OR they don't have a pre-filter value at all
      map.setFilter(layerName, [
        "all",
        [
          ">=",
          [
            "case",
            ["has", "Prefiltering Confidence Score"],
            ["get", "Prefiltering Confidence Score"],
            1,
          ],
          preFilteringConfidenceStart,
        ],
        [
          "<=",
          [
            "case",
            ["has", "Prefiltering Confidence Score"],
            ["get", "Prefiltering Confidence Score"],
            1,
          ],
          preFilteringConfidenceEnd,
        ],
      ]);
    });
  }, [
    annotationLayerName,
    map,
    mapLoaded,
    preFilteringConfidenceStart,
    preFilteringConfidenceEnd,
    namePrefixes,
  ]);

  return null;
};

export default VectorLayer;
